import { createContext, useContext } from "react";

/**
 * DE QUIEN ES LA BITACORA QUE SE ESTA MIRANDO (ADR 0005, matriz de permisos).
 *
 * El servidor acepta `?owner=<uuid>` en TODA lectura y responde 404 —no 403— a
 * quien no puede ver la de otro. Aqui vive lo elegido, y vive en dos sitios a
 * la vez y a proposito:
 *
 *   - En este contexto, para que cada hook de lectura lo anada a su peticion y a
 *     su clave de cache sin que cada pantalla tenga que acordarse.
 *   - En la URL (`?owner=`), para que un enlace se pueda compartir y una recarga
 *     no lo pierda. `OwnerProvider` mantiene los dos de acuerdo.
 *
 * `undefined` significa LA PROPIA. Es el valor por defecto del contexto, y lo
 * que ve cualquier hook que se monte fuera del proveedor: sin dueno elegido, la
 * peticion sale sin parametro y el servidor devuelve la del que pregunta.
 *
 * Este archivo no importa nada de la aplicacion, solo React: los hooks de
 * `api/` lo importan a el, y el necesita no importarlos a ellos.
 */

export const OWNER_PARAM = "owner";

export type OwnerSelection = {
  /** El dueno cuya bitacora se mira, o `undefined` si es la propia. */
  ownerId: string | undefined;
  /**
   * Elige otro dueno, o `undefined` para volver a la propia. Con `pathname` la
   * eleccion se hace navegando a esa ruta, que es lo que hace falta desde una
   * pantalla que no tiene sentido para otro dueno.
   */
  setOwner: (ownerId: string | undefined, pathname?: string) => void;
};

export const OwnerContext = createContext<OwnerSelection>({
  ownerId: undefined,
  setOwner: () => {},
});

export function useOwnerSelection(): OwnerSelection {
  return useContext(OwnerContext);
}

/** Lo unico que necesitan los hooks de lectura. */
export function useOwnerId(): string | undefined {
  return useContext(OwnerContext).ownerId;
}

/**
 * El trozo de la clave de cache que distingue a un dueno de otro. Va SIEMPRE,
 * tambien para la propia ("me"): si la propia no llevara nada, la clave
 * `["trades", filtros]` de hoy y la `["trades", uuid, filtros]` de otro
 * tendrian formas distintas y una invalidacion por prefijo podria alcanzar a
 * una y no a la otra.
 */
export function ownerKey(ownerId: string | undefined): string {
  return ownerId ?? "me";
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Un `?owner=` tal como llega de la URL. Lo que no es un uuid se descarta
 * aqui: el enlazador del servidor responderia 400 a `?owner=cualquiera`, y ese
 * 400 saldria en TODAS las lecturas de la pantalla a la vez.
 */
export function parseOwnerId(raw: string | null | undefined): string | undefined {
  if (raw === null || raw === undefined) return undefined;

  const text = raw.trim();

  return UUID.test(text) ? text.toLowerCase() : undefined;
}

/**
 * El dueno que de verdad se va a pedir.
 *
 *   - El propio identificador es "la propia": `?owner=<yo>` y sin parametro son
 *     la misma peticion, y tratarlos igual evita dos caches con lo mismo.
 *   - Sin permiso para leer la de otro no se pide otra. NO es una guarda nueva:
 *     el servidor responde 404 a un Trader con `?owner=` ajeno; lo que se evita
 *     es que un enlace compartido por error deje la bitacora de un Trader en
 *     "no se encontro" sin que el sepa por que.
 */
export function effectiveOwner(
  requested: string | undefined,
  meId: string | undefined,
  canReadOthers: boolean,
): string | undefined {
  if (requested === undefined) return undefined;
  if (meId !== undefined && requested === meId.toLowerCase()) return undefined;

  return canReadOthers ? requested : undefined;
}

/**
 * Lo que alguien pega en "Otra bitacora": el identificador a secas o el enlace
 * entero que le compartieron, con su `?owner=` dentro.
 */
export function ownerFromInput(text: string): string | undefined {
  const direct = parseOwnerId(text);

  if (direct !== undefined) return direct;

  try {
    const url = new URL(text.trim(), "http://localhost");

    return parseOwnerId(url.searchParams.get(OWNER_PARAM));
  } catch {
    return undefined;
  }
}

/**
 * La query string con el dueno puesto o quitado, sin tocar el resto de
 * parametros: `?pendientes=abiertas` es de la bitacora, no del dueno, y
 * cambiar de dueno no tiene por que quitar el filtro.
 */
export function withOwner(search: string, ownerId: string | undefined): string {
  const params = new URLSearchParams(search);

  if (ownerId === undefined) {
    params.delete(OWNER_PARAM);
  } else {
    params.set(OWNER_PARAM, ownerId);
  }

  const text = params.toString();

  return text === "" ? "" : `?${text}`;
}

/** Los ocho primeros caracteres, para nombrar un dueno del que no se sabe mas. */
export function shortOwnerId(ownerId: string): string {
  return `${ownerId.slice(0, 8)}…`;
}

/**
 * Lo que hay que hacer para que el dueno elegido y el `?owner=` de la URL
 * digan lo mismo, decidido como funcion pura para poder recorrerlo sin React.
 *
 *   - `strip`: la URL trae un `?owner=` que no vale (mal formado, el propio, o
 *     de quien no puede mirar otras): se quita de la URL y la eleccion vuelve a
 *     "la propia".
 *   - `adopt`: la URL trae un dueno valido distinto del elegido: MANDA LA URL
 *     (enlace compartido, recarga, "atras").
 *   - `restore`: la URL no trae el parametro y hay un dueno elegido: se repone,
 *     porque quien navego (un `NavLink`, la campana) no sabe del dueno.
 *   - `keep`: nada que hacer.
 *
 * `navigating` es la clave de todo (BUGLOG 2026-09-12): entre que `setOwner`
 * cambia la eleccion y el enrutador aplica la URL nueva hay un repintado con la
 * URL VIEJA. Sin esta bandera, ese repintado "adoptaba" el dueno viejo de la
 * URL, y al llegar la URL nueva sin parametro lo "restauraba": «Volver a la
 * mia» volvia a la de otro. Mientras la navegacion propia no ha llegado, la
 * URL que se ve es la de antes y no manda.
 */
export type OwnerSync =
  | { readonly kind: "keep" }
  | { readonly kind: "strip" }
  | { readonly kind: "adopt"; readonly ownerId: string | undefined }
  | { readonly kind: "restore"; readonly ownerId: string };

export function syncOwner(state: {
  readonly inUrl: boolean;
  readonly fromUrl: string | undefined;
  readonly chosen: string | undefined;
  readonly navigating: boolean;
}): OwnerSync {
  if (state.navigating) return { kind: "keep" };

  if (state.inUrl) {
    if (state.fromUrl === undefined) return { kind: "strip" };

    return state.fromUrl === state.chosen
      ? { kind: "keep" }
      : { kind: "adopt", ownerId: state.fromUrl };
  }

  return state.chosen === undefined ? { kind: "keep" } : { kind: "restore", ownerId: state.chosen };
}
